import type * as THREE from 'three';
import { bus } from '../core/EventBus';
import type { Checksum, Checksummable } from '../core/Checksum';
import type { Mission, MissionAction, MissionEvent, MissionTrigger } from './schema';
import type { ObjectiveTracker } from './Objectives';

/**
 * The trigger–action system (SPEC §3.10.1).
 *
 * One small system covering "essentially all narrative and pacing needs" — which only works if it
 * stays small. Triggers are pure predicates over a world snapshot; actions are a flat switch. No
 * mission gets to add code, so anything a mission needs must be expressible here.
 *
 * Evaluated at 10 Hz with the objectives. That rate is deliberate: a zone trigger tested at 120 Hz
 * would cost twelve times as much to tell you the same thing, and 100 ms of latency on a radio
 * call is imperceptible.
 */

export interface TriggerWorld {
  readonly positionM: THREE.Vector3;
  readonly elapsedS: number;
  readonly altitudeAglM: number;
  readonly integrity: number;
  /** Threat id → current state machine label, for `threatState`. */
  readonly threatStates: ReadonlyMap<string, string>;
}

/** A radio call in flight, surfaced to the HUD and (at M9) to audio. */
export interface RadioMessage {
  readonly speaker: string;
  readonly text: string;
  /** Seconds remaining on screen. */
  remainingS: number;
}

export interface TriggerEffects {
  /** Threat ids switched on or off this mission. */
  readonly activeThreats: Set<string>;
  /** Waypoint ids currently visible. */
  readonly revealedWaypoints: Set<string>;
  /** The message on screen, or null. */
  radio: RadioMessage | null;
  /** Weather patches applied, latest wins. */
  readonly weather: Record<string, number>;
  /** Threats a mission asked to spawn mid-flight. */
  readonly spawned: { type: string; position: readonly [number, number, number] }[];
}

export class TriggerSystem implements Checksummable {
  readonly effects: TriggerEffects;

  private readonly events: readonly MissionEvent[];
  private readonly fired: boolean[];
  private readonly objectives: ObjectiveTracker;

  constructor(mission: Mission, objectives: ObjectiveTracker) {
    this.events = mission.events;
    this.fired = new Array<boolean>(mission.events.length).fill(false);
    this.objectives = objectives;

    const activeThreats = new Set<string>();
    for (let i = 0; i < mission.threats.length; i += 1) {
      if (mission.threats[i].active) activeThreats.add(mission.threats[i].id);
    }
    // A waypoint with no `revealedFrom` is visible from the start; the rest wait for their event.
    const revealedWaypoints = new Set<string>();
    for (let i = 0; i < mission.route.length; i += 1) {
      if (mission.route[i].revealedFrom === undefined) revealedWaypoints.add(mission.route[i].id);
    }

    this.effects = {
      activeThreats,
      revealedWaypoints,
      radio: null,
      weather: {},
      spawned: [],
    };
  }

  /** Step the radio timer. Called every frame with the render delta, not the 10 Hz tick. */
  tickRadio(dtS: number): void {
    const radio = this.effects.radio;
    if (radio === null) return;
    radio.remainingS -= dtS;
    if (radio.remainingS <= 0) this.effects.radio = null;
  }

  /** Evaluate every event. Called at `MISSION.evaluateHz`. */
  update(world: TriggerWorld): void {
    for (let i = 0; i < this.events.length; i += 1) {
      const event = this.events[i];
      if (event.once && this.fired[i]) continue;
      if (!this.test(event.trigger, world)) continue;
      this.fired[i] = true;
      for (let j = 0; j < event.actions.length; j += 1) this.apply(event.actions[j]);
    }
  }

  private test(trigger: MissionTrigger, world: TriggerWorld): boolean {
    switch (trigger.t) {
      case 'zone': {
        const dx = world.positionM.x - trigger.center[0];
        const dy = world.positionM.y - trigger.center[1];
        const dz = world.positionM.z - trigger.center[2];
        return dx * dx + dy * dy + dz * dz <= trigger.radius * trigger.radius;
      }
      case 'time':
        return world.elapsedS >= trigger.seconds;
      case 'objective': {
        const status = this.objectives.find(trigger.ref);
        return status !== null && status.state === trigger.state;
      }
      case 'altitude': {
        // Both bounds present means a band, which is how a mission asks "are they in the weeds
        // but not on the ground".
        if (trigger.below !== undefined && world.altitudeAglM >= trigger.below) return false;
        if (trigger.above !== undefined && world.altitudeAglM <= trigger.above) return false;
        return trigger.below !== undefined || trigger.above !== undefined;
      }
      case 'threatState':
        return world.threatStates.get(trigger.ref) === trigger.state;
      case 'damage':
        return 1 - world.integrity > trigger.above;
      default:
        return false;
    }
  }

  private apply(action: MissionAction): void {
    switch (action.a) {
      case 'activateThreat':
        this.effects.activeThreats.add(action.ref);
        break;
      case 'deactivateThreat':
        this.effects.activeThreats.delete(action.ref);
        break;
      case 'spawn':
        this.effects.spawned.push({ type: action.type, position: action.position });
        break;
      case 'radio':
        this.effects.radio = {
          speaker: action.speaker,
          text: action.text,
          remainingS: action.durationS,
        };
        bus.emit('mission:radio', { speaker: action.speaker, text: action.text });
        break;
      case 'revealWaypoint':
        this.effects.revealedWaypoints.add(action.ref);
        break;
      case 'setObjective':
        this.objectives.set(action.ref, action.state);
        break;
      case 'setWeather':
        Object.assign(this.effects.weather, action.patch);
        break;
      default:
        break;
    }
  }

  checksum(into: Checksum): void {
    for (let i = 0; i < this.fired.length; i += 1) into.addInt(this.fired[i] ? 1 : 0);
  }
}
